import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { 
  Users, 
  FileCheck, 
  Clock,
  Loader2,
  Search,
  Eye,
  MoreHorizontal,
  MessageSquare,
  CheckCircle,
  XCircle,
  HelpCircle,
  Plane,
  RefreshCw,
  Plus,
  Briefcase
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { useNavigate } from 'react-router-dom';
import { useMyPartnerProfile, usePartnerCandidates, useUpdateCandidateStatus, useUpdateIntegration, usePartnerNeeds, useCreatePartnerNeed, useDeletePartnerNeed } from '../../lib/hooks';
import { useState } from 'react';
import { toast } from 'sonner';
import { useLanguage } from '../../contexts/LanguageContext';
import { t } from '../../lib/translations';

const PartnerDashboard = () => {
  const { user } = useAuth();
  const { lang } = useLanguage();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusDialog, setStatusDialog] = useState({ open: false, candidate: null });
  const [integrationDialog, setIntegrationDialog] = useState({ open: false, candidate: null });
  const [needDialog, setNeedDialog] = useState({ open: false });
  const [newStatus, setNewStatus] = useState('');
  const [statusNotes, setStatusNotes] = useState('');
  const [integrationStage, setIntegrationStage] = useState('');
  const [arrivalConfirmed, setArrivalConfirmed] = useState(false);
  const [arrivalDate, setArrivalDate] = useState('');
  const [integrationNotes, setIntegrationNotes] = useState('');
  const [needForm, setNeedForm] = useState({ title: '', type: 'stage', description: '', available_spots: 1, requirements: '' });

  const { data: profile, isLoading: profileLoading } = useMyPartnerProfile();
  const { data: candidates = [], isLoading: candidatesLoading } = usePartnerCandidates(profile?.id);
  const { data: needs = [], isLoading: needsLoading } = usePartnerNeeds(profile?.id);
  const updateStatus = useUpdateCandidateStatus(profile?.id);
  const updateIntegration = useUpdateIntegration(profile?.id);
  const createNeed = useCreatePartnerNeed(profile?.id);
  const deleteNeed = useDeletePartnerNeed(profile?.id);

  const loading = profileLoading || candidatesLoading;

  const filteredCandidates = candidates.filter(c => 
    `${c.first_name} ${c.last_name}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (c.visa_objective || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const stats = {
    assigned: candidates.length,
    complete: candidates.filter(c => c.dossier_completion >= 100).length,
    accepted: candidates.filter(c => c.placement_status === 'accepted').length,
    pending: candidates.filter(c => c.placement_status === 'pending').length
  };

  const placementStatusLabels = {
    pending: 'En attente',
    accepted: 'Accepté',
    needs_info: 'Info requise',
    rejected: 'Refusé'
  };

  const placementStatusColors = {
    pending: 'bg-warning/10 text-warning',
    accepted: 'bg-success/10 text-success',
    needs_info: 'bg-primary/10 text-primary',
    rejected: 'bg-destructive/10 text-destructive'
  };

  const needTypeLabels = {
    stage: 'Stage',
    temps_partiel: 'Temps partiel',
    temps_plein: 'Temps plein',
    formation: 'Formation',
    programme: 'Programme',
    // legacy
    position: 'Poste',
    program: 'Programme',
  };

  const needTypeColors = {
    stage: 'bg-blue-100 text-blue-700 border-blue-200',
    temps_partiel: 'bg-purple-100 text-purple-700 border-purple-200',
    temps_plein: 'bg-green-100 text-green-700 border-green-200',
    formation: 'bg-orange-100 text-orange-700 border-orange-200',
    programme: 'bg-secondary/10 text-secondary border-secondary/20',
    position: 'bg-gray-100 text-gray-700 border-gray-200',
    program: 'bg-secondary/10 text-secondary border-secondary/20',
  };

  const integrationStageLabels = {
    pre_departure: 'Pré-départ',
    arrived: 'Arrivé',
    settled: 'Installé',
    completed: 'Intégration terminée'
  };

  const integrationStageColors = {
    pre_departure: 'bg-secondary/10 text-secondary',
    arrived: 'bg-primary/10 text-primary',
    settled: 'bg-accent/10 text-accent',
    completed: 'bg-success/10 text-success'
  };

  const handleOpenStatusDialog = (candidate) => {
    setStatusDialog({ open: true, candidate });
    setNewStatus(candidate.placement_status || 'pending');
    setStatusNotes(candidate.notes || '');
  };

  const handleUpdateStatus = async () => {
    if (!statusDialog.candidate) return;
    try {
      await updateStatus.mutateAsync({
        studentId: statusDialog.candidate.id,
        data: { status: newStatus, notes: statusNotes }
      });
      toast.success('Statut mis à jour');
      setStatusDialog({ open: false, candidate: null });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    }
  };

  const handleOpenIntegrationDialog = (candidate) => {
    setIntegrationDialog({ open: true, candidate });
    setIntegrationStage(candidate.integration_stage || 'pre_departure');
    setArrivalConfirmed(false);
    setArrivalDate('');
    setIntegrationNotes('');
  };

  const handleUpdateIntegration = async () => {
    if (!integrationDialog.candidate) return;
    try {
      await updateIntegration.mutateAsync({
        studentId: integrationDialog.candidate.id,
        data: { 
          stage: integrationStage, 
          arrival_confirmed: arrivalConfirmed,
          arrival_date: arrivalDate || null,
          notes: integrationNotes || null
        }
      });
      toast.success('Intégration mise à jour');
      setIntegrationDialog({ open: false, candidate: null });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    }
  };

  const handleCreateNeed = async (e) => {
    e.preventDefault();
    try {
      await createNeed.mutateAsync({
        ...needForm,
        status: 'open'
      });
      toast.success('Offre créée avec succès');
      setNeedDialog({ open: false });
      setNeedForm({ title: '', type: 'position', description: '', available_spots: 1, requirements: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    }
  };

  const handleDeleteNeed = async (needId) => {
    try {
      await deleteNeed.mutateAsync(needId);
      toast.success('Offre supprimée');
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="partner-dashboard">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-foreground">
            Bonjour, {user?.first_name}!
          </h1>
          <p className="text-muted-foreground mt-1">
            {profile?.organization_name} • {profile?.city}, {profile?.country}
          </p>
        </div>
        <Button onClick={() => setNeedDialog({ open: true })} className="btn-active" data-testid="add-need-btn">
          <Plus className="w-4 h-4 mr-2" />
          Publier une offre
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="border-0 shadow-sm card-hover">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-1">{t('partner_dash_assigned', lang)}</p>
                <p className="text-3xl font-bold font-heading">{stats.assigned}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-primary flex items-center justify-center">
                <Users className="w-6 h-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm card-hover">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Dossiers complets</p>
                <p className="text-3xl font-bold font-heading">{stats.complete}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-success flex items-center justify-center">
                <FileCheck className="w-6 h-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm card-hover">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Acceptés</p>
                <p className="text-3xl font-bold font-heading text-success">{stats.accepted}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-secondary flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm card-hover">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-1">{t('status_pending', lang)}</p>
                <p className="text-3xl font-bold font-heading">{stats.pending}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-warning flex items-center justify-center">
                <Clock className="w-6 h-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* My Offers Section - toujours visible */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="font-heading text-xl flex items-center gap-2">
                <Briefcase className="w-5 h-5" />
                Mes offres
              </CardTitle>
              <CardDescription>Postes et programmes publiés ({needs.length})</CardDescription>
            </div>
            <Button onClick={() => setNeedDialog({ open: true })} variant="outline" size="sm" data-testid="add-need-btn-inline">
              <Plus className="w-4 h-4 mr-2" />
              Publier une offre
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {needsLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
            </div>
          ) : needs.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Briefcase className="w-10 h-10 mx-auto mb-3 opacity-40" />
              <p className="font-medium">Aucune offre publiée</p>
              <p className="text-sm mt-1">Cliquez sur "Publier une offre" pour créer votre première offre</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {needs.map((need) => (
                <div key={need.id} className="p-4 border rounded-lg bg-muted/30">
                  <div className="flex items-start justify-between">
                    <div>
                      <h4 className="font-medium">{need.title}</h4>
                      <Badge variant="outline" className={`mt-1 ${needTypeColors[need.type] || 'bg-gray-100 text-gray-700'}`}>
                        {needTypeLabels[need.type] || need.type}
                      </Badge>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDeleteNeed(need.id)}
                      className="text-destructive hover:text-destructive"
                    >
                      <XCircle className="w-4 h-4" />
                    </Button>
                  </div>
                  {need.description && (
                    <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{need.description}</p>
                  )}
                  <p className="text-xs text-muted-foreground mt-2">
                    {need.available_spots} place(s) disponible(s)
                  </p>
                </div>
              ))}
            </div>
          )}
          </CardContent>
        </Card>

      {/* Candidates Table */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <CardTitle className="font-heading text-xl">{t('partner_dash_my_candidates', lang)}</CardTitle>
              <CardDescription>Liste des étudiants qui vous sont assignés</CardDescription>
            </div>
            <div className="relative w-full md:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder={t("action_search", lang)}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="candidate-search-input"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {filteredCandidates.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="font-semibold text-lg mb-1">{lang === 'de' ? 'Kein Kandidat' : 'Aucun candidat'}</h3>
              <p className="text-muted-foreground">
                {candidates.length === 0 
                  ? "Vous n'avez pas encore de candidats assignés"
                  : "Aucun candidat ne correspond à votre recherche"
                }
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('students_name', lang)}</TableHead>
                  <TableHead className="hidden md:table-cell">{t('dossier_detail_visa', lang)}</TableHead>
                  <TableHead>{lang === 'de' ? 'Akte' : 'Dossier'}</TableHead>
                  <TableHead>{t('students_status', lang)}</TableHead>
                  <TableHead>Intégration</TableHead>
                  <TableHead className="hidden lg:table-cell">{t('students_level', lang)}</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredCandidates.map((candidate) => (
                  <TableRow key={candidate.id} className="hover:bg-muted/50">
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center">
                          <span className="text-primary font-semibold text-sm">
                            {candidate.first_name[0]}{candidate.last_name[0]}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium">{candidate.first_name} {candidate.last_name}</p>
                          <p className="text-xs text-muted-foreground">{candidate.email}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      <span className="text-sm">{candidate.visa_objective || 'Non spécifié'}</span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className="w-16 h-2 bg-muted rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-primary transition-all" 
                            style={{ width: `${candidate.dossier_completion || 0}%` }}
                          />
                        </div>
                        <span className="text-xs text-muted-foreground">
                          {candidate.dossier_completion || 0}%
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary" className={placementStatusColors[candidate.placement_status]}>
                        {placementStatusLabels[candidate.placement_status] || 'En attente'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary" className={integrationStageColors[candidate.integration_stage]}>
                        {integrationStageLabels[candidate.integration_stage] || 'Pré-départ'}
                      </Badge>
                    </TableCell>
                    <TableCell className="hidden lg:table-cell">
                      <Badge variant="outline">{candidate.german_level || 'N/A'}</Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" data-testid={`candidate-actions-${candidate.id}`}>
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleOpenStatusDialog(candidate)}>
                            <RefreshCw className="w-4 h-4 mr-2" />
                            Changer le statut
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleOpenIntegrationDialog(candidate)}>
                            <Plane className="w-4 h-4 mr-2" />
                            Suivi d'intégration
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => navigate('/messages')}>
                            <MessageSquare className="w-4 h-4 mr-2" />
                            Envoyer un message
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Status Update Dialog */}
      <Dialog open={statusDialog.open} onOpenChange={(open) => setStatusDialog({ open, candidate: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">Mettre à jour le statut</DialogTitle>
            <DialogDescription>
              Candidat: {statusDialog.candidate?.first_name} {statusDialog.candidate?.last_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Statut de la candidature</Label>
              <Select value={newStatus} onValueChange={setNewStatus}>
                <SelectTrigger data-testid="status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-warning" />
                      En attente
                    </div>
                  </SelectItem>
                  <SelectItem value="accepted">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-success" />
                      Accepté
                    </div>
                  </SelectItem>
                  <SelectItem value="needs_info">
                    <div className="flex items-center gap-2">
                      <HelpCircle className="w-4 h-4 text-primary" />
                      Information requise
                    </div>
                  </SelectItem>
                  <SelectItem value="rejected">
                    <div className="flex items-center gap-2">
                      <XCircle className="w-4 h-4 text-destructive" />
                      Refusé
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Notes (optionnel)</Label>
              <Textarea
                value={statusNotes}
                onChange={(e) => setStatusNotes(e.target.value)}
                placeholder="Ajouter des notes..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStatusDialog({ open: false, candidate: null })}>
              Annuler
            </Button>
            <Button onClick={handleUpdateStatus} disabled={updateStatus.isPending}>
              {updateStatus.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Integration Update Dialog */}
      <Dialog open={integrationDialog.open} onOpenChange={(open) => setIntegrationDialog({ open, candidate: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">Suivi d'intégration</DialogTitle>
            <DialogDescription>
              Candidat: {integrationDialog.candidate?.first_name} {integrationDialog.candidate?.last_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Étape d'intégration</Label>
              <Select value={integrationStage} onValueChange={setIntegrationStage}>
                <SelectTrigger data-testid="integration-stage-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pre_departure">Pré-départ</SelectItem>
                  <SelectItem value="arrived">Arrivé en Allemagne</SelectItem>
                  <SelectItem value="settled">{t('arrivals_status_settled', lang)}</SelectItem>
                  <SelectItem value="completed">Intégration terminée</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="arrival-confirmed"
                checked={arrivalConfirmed}
                onChange={(e) => setArrivalConfirmed(e.target.checked)}
                className="rounded"
              />
              <Label htmlFor="arrival-confirmed">Confirmer l'arrivée</Label>
            </div>
            {arrivalConfirmed && (
              <div className="space-y-2">
                <Label>Date d'arrivée</Label>
                <Input
                  type="date"
                  value={arrivalDate}
                  onChange={(e) => setArrivalDate(e.target.value)}
                />
              </div>
            )}
            <div className="space-y-2">
              <Label>Notes (optionnel)</Label>
              <Textarea
                value={integrationNotes}
                onChange={(e) => setIntegrationNotes(e.target.value)}
                placeholder="Notes sur l'intégration..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIntegrationDialog({ open: false, candidate: null })}>
              Annuler
            </Button>
            <Button onClick={handleUpdateIntegration} disabled={updateIntegration.isPending}>
              {updateIntegration.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Need Dialog */}
      <Dialog open={needDialog.open} onOpenChange={(open) => setNeedDialog({ open })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">Publier une offre</DialogTitle>
            <DialogDescription>
              Créez un poste ou un programme pour attirer des candidats
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateNeed} className="space-y-4">
            <div className="space-y-2">
              <Label>Titre *</Label>
              <Input
                value={needForm.title}
                onChange={(e) => setNeedForm({ ...needForm, title: e.target.value })}
                placeholder="Ex: Stage en développement web"
                required
              />
            </div>
            <div className="space-y-2">
              <Label>{t('partners_type', lang)}</Label>
              <Select value={needForm.type} onValueChange={(v) => setNeedForm({ ...needForm, type: v })}>
                <SelectTrigger data-testid="need-type-select">
                  <SelectValue placeholder="Sélectionner un type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="stage">Stage</SelectItem>
                  <SelectItem value="temps_partiel">Temps partiel</SelectItem>
                  <SelectItem value="temps_plein">Temps plein</SelectItem>
                  <SelectItem value="formation">Formation</SelectItem>
                  <SelectItem value="programme">Programme</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t('form_description', lang)}</Label>
              <Textarea
                value={needForm.description}
                onChange={(e) => setNeedForm({ ...needForm, description: e.target.value })}
                placeholder="Décrivez l'offre..."
                rows={3}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Places disponibles</Label>
                <Input
                  type="number"
                  min="1"
                  value={needForm.available_spots}
                  onChange={(e) => setNeedForm({ ...needForm, available_spots: parseInt(e.target.value) })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Prérequis</Label>
              <Textarea
                value={needForm.requirements}
                onChange={(e) => setNeedForm({ ...needForm, requirements: e.target.value })}
                placeholder="Ex: Niveau B1 minimum, connaissances en..."
                rows={2}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setNeedDialog({ open: false })}>
                Annuler
              </Button>
              <Button type="submit" disabled={createNeed.isPending}>
                {createNeed.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Publier
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PartnerDashboard;
